import { Slot, Slottable } from '@radix-ui/react-slot';
import type { VariantProps } from 'class-variance-authority';
import type { ComponentPropsWithRef } from 'react';
import { cn } from '../lib/cn';
import { buttonVariants } from './button-variants';
import { Spinner } from './Spinner';

export interface ButtonProps
  extends ComponentPropsWithRef<'button'>, VariantProps<typeof buttonVariants> {
  /**
   * Render the child element instead of a `<button>`, forwarding these styles
   * onto it. For a link that should look like a button — a real anchor keeps
   * middle-click, copy-link and the correct role.
   */
  asChild?: boolean | undefined;
  /**
   * Busy state. Disables the control, sets `aria-busy`, and prefixes a
   * decorative spinner. The label stays in the DOM rather than being swapped
   * for the spinner, so the accessible name does not change mid-interaction
   * and the button does not resize.
   */
  loading?: boolean | undefined;
}

export function Button({
  asChild = false,
  loading = false,
  variant,
  size,
  block,
  className,
  disabled,
  children,
  ...props
}: ButtonProps) {
  const Component = asChild ? Slot : 'button';
  const isDisabled = disabled === true || loading;

  return (
    <Component
      className={cn(buttonVariants({ variant, size, block }), className)}
      // Slot forwards onto an arbitrary element, which may not accept
      // `disabled`; aria-disabled is what actually communicates the state
      // there, and the class above removes pointer events either way.
      disabled={asChild ? undefined : isDisabled}
      aria-disabled={isDisabled || undefined}
      aria-busy={loading || undefined}
      {...props}
    >
      {loading ? <Spinner size="sm" /> : null}
      {/* Slottable marks which child Slot should merge onto, so the spinner can
          sit beside it. Without it, `asChild` sees two children and throws. */}
      <Slottable>{children}</Slottable>
    </Component>
  );
}
